import { priceAt } from '../data/seed/seedReference';
import type { Commitment, LedgerEntry, RegistryState, UnitClass } from '../data/types';

export interface Holding {
  holderId: string;
  fundId: string;
  classId: string;
  units: number;
  value: number;
  costBase: number;
  commitment?: number;
  uncalled?: number;
  firstDate: string;
}

const UNIT_CHANGING_TYPES = new Set<LedgerEntry['type']>([
  'ISSUE',
  'CALL_PAYMENT',
  'DRP_ISSUE',
  'REDEMPTION',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'MIGRATION_OPENING',
  'ADJUSTMENT',
  'CAPITAL_RETURN',
]);

const COST_ADDING_TYPES = new Set<LedgerEntry['type']>([
  'ISSUE',
  'CALL_PAYMENT',
  'DRP_ISSUE',
  'TRANSFER_IN',
  'MIGRATION_OPENING',
]);

const COST_REDUCING_TYPES = new Set<LedgerEntry['type']>([
  'REDEMPTION',
  'TRANSFER_OUT',
  'CAPITAL_RETURN',
]);

export function changesUnits(entry: LedgerEntry): boolean {
  return UNIT_CHANGING_TYPES.has(entry.type);
}

export function unitsAt(
  ledger: LedgerEntry[],
  holderId: string,
  classId: string,
  asAt: string,
): number {
  let total = 0;
  for (const entry of ledger) {
    if (
      entry.holderId === holderId &&
      entry.classId === classId &&
      entry.effectiveDate <= asAt &&
      changesUnits(entry)
    ) {
      total += entry.units;
    }
  }
  return Math.round(total * 10_000) / 10_000;
}

export function computeHoldings(
  ledger: LedgerEntry[],
  classes: UnitClass[],
  commitments: Commitment[],
  asAt: string,
): Holding[] {
  const byKey = new Map<string, Holding>();
  const classById = new Map(classes.map((unitClass) => [unitClass.id, unitClass]));
  for (const entry of ledger) {
    if (entry.effectiveDate > asAt) continue;
    const key = `${entry.holderId}|${entry.classId}`;
    let holding = byKey.get(key);
    if (!holding) {
      holding = {
        holderId: entry.holderId,
        fundId: entry.fundId,
        classId: entry.classId,
        units: 0,
        value: 0,
        costBase: 0,
        firstDate: entry.effectiveDate,
      };
      byKey.set(key, holding);
    }
    if (changesUnits(entry)) holding.units += entry.units;
    if (COST_ADDING_TYPES.has(entry.type)) holding.costBase += entry.amount;
    if (entry.type === 'REDEMPTION' || entry.type === 'TRANSFER_OUT') {
      holding.costBase -= entry.amount;
    }
    if (entry.type === 'CAPITAL_RETURN') holding.costBase -= entry.amount;
    if (entry.type === 'ADJUSTMENT' && entry.reversedType) {
      if (COST_ADDING_TYPES.has(entry.reversedType)) holding.costBase -= entry.amount;
      if (COST_REDUCING_TYPES.has(entry.reversedType)) holding.costBase += entry.amount;
    }
    if (entry.effectiveDate < holding.firstDate) holding.firstDate = entry.effectiveDate;
  }
  for (const commitment of commitments) {
    if (commitment.date > asAt) continue;
    const key = `${commitment.holderId}|${commitment.classId}`;
    const holding = byKey.get(key);
    if (holding) {
      holding.commitment = (holding.commitment ?? 0) + commitment.amount;
    }
  }
  const result: Holding[] = [];
  for (const holding of byKey.values()) {
    holding.units = Math.round(holding.units * 10_000) / 10_000;
    if (holding.units <= 0.0001 && !holding.commitment) continue;
    const unitClass = classById.get(holding.classId);
    const price = unitClass ? priceAt(unitClass, asAt) : 1;
    holding.value = Math.round(holding.units * price * 100) / 100;
    if (holding.commitment !== undefined) {
      holding.uncalled = Math.max(0, holding.commitment - holding.costBase);
    }
    result.push(holding);
  }
  return result;
}

export function holdingsForState(state: RegistryState, asAt: string): Holding[] {
  return computeHoldings(state.ledger, state.classes, state.commitments, asAt);
}

export function incomeForHolder(ledger: LedgerEntry[], holderId: string, from: string, to: string) {
  let net = 0;
  let withheld = 0;
  for (const entry of ledger) {
    if (entry.holderId !== holderId || entry.effectiveDate < from || entry.effectiveDate > to) {
      continue;
    }
    if (entry.type === 'DISTRIBUTION') net += entry.amount;
    if (entry.type === 'WITHHOLDING') withheld += entry.amount;
    if (entry.type === 'ADJUSTMENT' && entry.reversedType === 'DISTRIBUTION') net -= entry.amount;
    if (entry.type === 'ADJUSTMENT' && entry.reversedType === 'WITHHOLDING')
      withheld -= entry.amount;
  }
  return { net, withheld, gross: net + withheld };
}

export interface ClosedHolding {
  holderId: string;
  fundId: string;
  classId: string;
  invested: number;
  returned: number;
  income: number;
  firstDate: string;
  lastDate: string;
}

export function closedHoldings(ledger: LedgerEntry[], open: Holding[]): ClosedHolding[] {
  const openKeys = new Set(open.map((holding) => `${holding.holderId}|${holding.classId}`));
  const byKey = new Map<string, ClosedHolding>();
  for (const entry of ledger) {
    const key = `${entry.holderId}|${entry.classId}`;
    if (openKeys.has(key)) continue;
    let item = byKey.get(key);
    if (!item) {
      item = {
        holderId: entry.holderId,
        fundId: entry.fundId,
        classId: entry.classId,
        invested: 0,
        returned: 0,
        income: 0,
        firstDate: entry.effectiveDate,
        lastDate: entry.effectiveDate,
      };
      byKey.set(key, item);
    }
    if (COST_ADDING_TYPES.has(entry.type)) item.invested += entry.amount;
    if (entry.type === 'CAPITAL_RETURN' || entry.type === 'REDEMPTION')
      item.returned += entry.amount;
    if (entry.type === 'DISTRIBUTION') item.income += entry.amount;
    if (entry.effectiveDate < item.firstDate) item.firstDate = entry.effectiveDate;
    if (entry.effectiveDate > item.lastDate) item.lastDate = entry.effectiveDate;
  }
  return Array.from(byKey.values()).filter((item) => item.returned > 0);
}
